import type { ProgramEvent, ProgramUserRsvp } from 'models';
import { useCallback } from 'react';
import { message } from 'ui';
import { useUpdateEventUserStatus } from '../queries';
import { isAfterJoinPeriod, isJoinPeriod } from '../utils';
import { useOnRequestEnterConference } from './useOnRequestEnterConference';
import { useRsvpStatusDropdownOptions } from './useRsvpStatusDropdownOptions';

export type ProgramEventData = { authorId?: string } & Pick<
  ProgramEvent,
  | 'bmgId'
  | 'changeRequests'
  | 'currentUserRole'
  | 'currentUserRsvp'
  | 'dialInInformation'
  | 'endTime'
  | 'id'
  | 'startTime'
  | 'status'
>;

export const useEventActions = ({ data }: { data: ProgramEventData }) => {
  const { isPending: isAccepting, mutate: acceptEvent } = useUpdateEventUserStatus();
  const { isPending: isRejecting, mutate: rejectEvent } = useUpdateEventUserStatus();
  const { isJoining, onRequestEnterConference } = useOnRequestEnterConference();

  const isPastEvent = isAfterJoinPeriod(data.endTime);
  const isOpenToResponse = !isPastEvent;

  const onEventActionClick = useCallback(
    (action: 'join' | ProgramUserRsvp) => {
      // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
      switch (action) {
        case 'going':
          if (isOpenToResponse) {
            acceptEvent({ id: data.id, statusToApply: action });
          } else {
            message.warn('This event has started already');
          }
          break;

        case 'join':
          onRequestEnterConference(data);
          break;

        case 'not_going':
          if (isOpenToResponse) {
            rejectEvent({ id: data.id, statusToApply: action });
          } else {
            message.warn('This event has started already');
          }
          break;

        default:
          console.error('Unhandled action:', action);
      }
    },
    [isOpenToResponse, onRequestEnterConference, data, acceptEvent, rejectEvent],
  );

  const { dropdownOptions } = useRsvpStatusDropdownOptions({
    authorId: data.authorId,
    endTime: data.endTime,
    onGoing() {
      onEventActionClick('going');
    },
    onNotGoing() {
      onEventActionClick('not_going');
    },
    rsvp: data.currentUserRsvp,
  });

  const onJoinButtonClick = useCallback(() => {
    if (isJoinPeriod(data)) {
      onEventActionClick('join');
      return;
    }

    if (data.currentUserRsvp === 'pending') {
      onEventActionClick('going');
      return;
    }

    console.error(
      'Join button clicked, but no action performed: Event is neither open to join nor is user RSVP pending.',
    );

    return null;
  }, [data, onEventActionClick]);

  const onRejectButtonClick = useCallback(() => {
    onEventActionClick('not_going');
  }, [onEventActionClick]);

  return {
    dropdownOptions,
    isAccepting,
    isJoining,
    isRejecting,
    onEventActionClick,
    onJoinButtonClick,
    onRejectButtonClick,
    onRequestEnterConference,
  };
};
